import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

const SEVERITY_BADGES = {
  high: 'bg-danger',
  medium: 'bg-warning text-dark',
  low: 'bg-info text-dark',
};

const STATUS_BADGES = {
  open: 'bg-danger',
  in_review: 'bg-warning text-dark',
  resolved: 'bg-success',
  dismissed: 'bg-secondary',
};


export default function Exceptions() {
  const [exceptions, setExceptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [msg, setMsg] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.exception_type = typeFilter;
    if (severityFilter) params.severity = severityFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;

    Promise.all([
      client.get('/exceptions/', { params }),
      client.get('/exceptions/stats'),
    ])
      .then(([ex, st]) => {
        setExceptions(ex.data.exceptions || []);
        setStats(st.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, typeFilter, severityFilter, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResolve = async () => {
    if (!resolveModal) return;
    setResolving(true);
    try {
      await client.patch(`/exceptions/${resolveModal.id}/resolve`, { notes: resolveNotes });
      setMsg('Exception resolved');
      setResolveModal(null);
      fetchData();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleDismiss = async (exc) => {
    try {
      await client.patch(`/exceptions/${exc.id}/dismiss`, { notes: '' });
      setMsg('Exception dismissed');
      fetchData();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value)); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(exceptions.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = exceptions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      {/* Stats cards */}
      {stats && (
        <div className="row g-3 mb-0">
          <div className="col-md-3">
            <div className="card text-center" style={{ borderLeft: `4px solid #dc3545` }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#dc3545' }}>{stats.total_open}</div>
                <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Open</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center" style={{ borderLeft: `4px solid #198754` }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#198754' }}>{stats.total_resolved}</div>
                <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Resolved</div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card">
              <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>By Type</div>
                  {typeFilter && (
                    <button
                      className="btn btn-sm btn-link p-0"
                      style={{ fontSize: '11px', color: '#6c757d' }}
                      onClick={() => setTypeFilter('')}
                    >
                      Clear filter ×
                    </button>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {(stats.by_type || []).map(t => {
                    const isActive = typeFilter === t.exception_type;
                    return (
                      <button
                        key={t.exception_type}
                        onClick={() => setTypeFilter(isActive ? '' : t.exception_type)}
                        className="badge border"
                        style={{
                          fontSize: '11px',
                          cursor: 'pointer',
                          backgroundColor: isActive ? THX_RED : '#f8f9fa',
                          color: isActive ? '#fff' : '#212529',
                          borderColor: isActive ? THX_RED : '#dee2e6',
                          fontWeight: isActive ? '600' : '400',
                        }}
                        title={`Filter by: ${t.exception_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
                      >
                        {t.exception_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: {t.total} ({t.open_count} open)
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {msg && <div className="alert alert-info py-2 mb-3" style={{ fontSize: '14px' }}>{msg}</div>}

      {/* Period filter */}
      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPage(1); }}
      />

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>Status</label>
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="in_review">In Review</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>Type</label>
              <select
                className="form-select form-select-sm"
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Types</option>
                {['missing_invoice','unmatched_supplier','duplicate','amount_mismatch','tax_mismatch',
                  'credit_note','low_confidence','unreadable','portal_link','currency_flag',
                  'balance_mismatch','no_po_match'].map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>Severity</label>
              <select
                className="form-select form-select-sm"
                value={severityFilter}
                onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}
              >
                <option value="">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header py-2">
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Exceptions ({exceptions.length})</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Description</th>
                    <th>Invoice / Supplier</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-muted py-4">No exceptions found</td></tr>
                  )}
                  {paginated.map(exc => (
                    <tr key={exc.id}>
                      <td>{exc.exception_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                      <td>
                        <span className={`badge ${SEVERITY_BADGES[exc.severity] || 'bg-secondary'}`}>
                          {exc.severity}
                        </span>
                      </td>
                      <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {exc.description}
                      </td>
                      <td>
                        {exc.invoice_number || exc.matched_supplier_name || exc.supplier_name || '–'}
                      </td>
                      <td>{exc.created_at ? new Date(exc.created_at).toLocaleDateString('en-GB') : '–'}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGES[exc.status] || 'bg-secondary'}`}>
                          {exc.status}
                        </span>
                      </td>
                      <td>
                        {exc.status === 'open' || exc.status === 'in_review' ? (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: THX_RED, color: '#fff', border: 'none', fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => { setResolveModal(exc); setResolveNotes(''); }}
                            >
                              Resolve
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              style={{ fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => handleDismiss(exc)}
                            >
                              Dismiss
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && exceptions.length > 0 && (
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            total={exceptions.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      {/* Resolve Modal */}
      {resolveModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontSize: '16px' }}>Resolve Exception</h5>
                <button type="button" className="close" onClick={() => setResolveModal(null)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '14px' }}><strong>Type:</strong> {resolveModal.exception_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                <p style={{ fontSize: '14px' }}><strong>Description:</strong> {resolveModal.description}</p>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '13px' }}>Resolution Notes</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={resolveNotes}
                    onChange={e => setResolveNotes(e.target.value)}
                    placeholder="Describe how this was resolved..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setResolveModal(null)}>Cancel</button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                  onClick={handleResolve}
                  disabled={resolving}
                >
                  {resolving ? 'Resolving...' : 'Mark Resolved'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
